import React, { useState, useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import Marquee from "react-fast-marquee";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  ShieldCheck, BookOpen, Briefcase, Crown, ArrowRight, Star, Check,
  CreditCard, GraduationCap, TrendingUp, Package, Compass, Menu, X, Quote,
  BadgeCheck, Clock, Users, Award, FileCheck, Download, Lock, Sparkles,
  Play, Scale,
} from "lucide-react";
import { GoldButton, GhostButton, Reveal, Overline, useDialog } from "../components/site/shared";
import { createLead, checklistInfo, CHECKLIST_DOWNLOAD_URL, createCheckout } from "../lib/api";
import ChatWidget from "../components/site/ChatWidget";
import Catalog from "../components/site/Catalog";

const IMG = {
  vault: "https://images.unsplash.com/photo-1777652062282-9b2985cd28d9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  library: "https://images.unsplash.com/photo-1779153304203-e0d044f63e53?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  boardroom: "https://images.unsplash.com/photo-1770819372116-31640041aa3b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  lounge: "https://images.unsplash.com/photo-1781955781178-6ea29c66e860?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
  trio: "https://customer-assets-m6fa6gv7.emergentagent.net/job_wealth-ecosystem-3d/artifacts/txaodo38_IMG_8389.jpeg",
  banner: "https://customer-assets-m6fa6gv7.emergentagent.net/job_wealth-ecosystem-3d/artifacts/rhfxwk17_IMG_9263.png",
  bossLady: "https://customer-assets-m6fa6gv7.emergentagent.net/job_wealth-ecosystem-3d/artifacts/3ej45ami_IMG_8252.jpeg",
  score1: "https://customer-assets-m6fa6gv7.emergentagent.net/job_wealth-ecosystem-3d/artifacts/tjjxy488_54F0D2C7-0D85-4BB6-B974-556AA5B87193.webp",
  score2: "https://customer-assets-m6fa6gv7.emergentagent.net/job_wealth-ecosystem-3d/artifacts/1ig7fmpq_0F225E00-A685-4DC8-A12E-E985785CA6FE.webp",
  founder: "https://customer-assets-m6fa6gv7.emergentagent.net/job_wealth-ecosystem-3d/artifacts/3ej45ami_IMG_8252.jpeg",
};

// MOCK placeholder intro video — replace VIDEO_SRC with BGM's real intro video URL.
const VIDEO_SRC = "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerJoyrides.mp4";

const SERVICE_LINKS = [
  { label: "Credit Repair", id: "services", icon: ShieldCheck },
  { label: "Credit Education", id: "services", icon: GraduationCap },
  { label: "Business Funding", id: "business", icon: Briefcase },
  { label: "Digital Products", id: "services", icon: Package },
  { label: "Financial Coaching", id: "services", icon: Compass },
];

const NAV = [
  ["HOME", "top"], ["CREDIT", "credit"], ["EDUCATION", "education"],
  ["BUSINESS", "business"], ["DIGITAL PRODUCTS", "services"], ["CONTACT", "contact"],
];

/* ============ Floating Nav ============ */
function FloatingNav() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const account = user && user.role === "admin" ? "/admin" : "/dashboard";
  const go = (id) => {
    setOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };
  return (
    <>
      <motion.nav
        initial={{ y: -40, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.4, duration: 0.8 }}
        className="fixed left-1/2 top-5 z-[500] -translate-x-1/2"
        data-testid="floating-nav"
      >
        <div className="glass flex items-center gap-1 rounded-full px-3 py-2 shadow-2xl">
          <button onClick={() => go("top")} className="px-3 font-display text-lg font-bold tracking-[0.15em] text-gold" data-testid="nav-logo">BGM</button>
          <div className="mx-1 hidden h-5 w-px bg-white/10 md:block" />
          <div className="hidden items-center md:flex">
            {NAV.map(([label, id]) => (
              <button key={id} data-testid={`nav-${id}`} onClick={() => go(id)}
                className="rounded-full px-4 py-1.5 font-body text-xs font-medium tracking-wide text-[#c9c9c9] transition-colors duration-300 hover:text-gold">
                {label}
              </button>
            ))}
          </div>
          <button onClick={() => go("contact")}
            className="ml-1 hidden rounded-full bg-[#d4af37] px-4 py-1.5 font-body text-xs font-semibold text-[#050505] transition-colors hover:bg-[#f3e5ab] md:block"
            data-testid="nav-cta">Get Started</button>
          {user ? (
            <button onClick={() => navigate(account)} data-testid="nav-account"
              className="ml-1 hidden rounded-full border border-[#d4af37]/40 px-4 py-1.5 font-body text-xs font-semibold text-gold hover:bg-[#d4af37]/10 md:block">My Account</button>
          ) : (
            <button onClick={() => navigate("/login")} data-testid="nav-login"
              className="ml-1 hidden rounded-full border border-white/15 px-4 py-1.5 font-body text-xs font-medium text-[#c9c9c9] hover:border-[#d4af37]/50 hover:text-gold md:block">Login</button>
          )}
          <button className="p-2 text-gold md:hidden" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </motion.nav>
      {open && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
          className="glass-solid fixed left-1/2 top-20 z-[499] w-[90%] max-w-sm -translate-x-1/2 rounded-2xl p-4 md:hidden" data-testid="nav-mobile-menu">
          {NAV.map(([label, id]) => (
            <button key={id} onClick={() => go(id)} data-testid={`nav-mobile-${id}`}
              className="block w-full rounded-lg px-4 py-3 text-left font-body text-sm text-[#e0e0e0] hover:bg-white/5 hover:text-gold">
              {label}
            </button>
          ))}
          <button onClick={() => navigate(user ? account : "/login")} data-testid="nav-mobile-account"
            className="mt-1 block w-full rounded-lg bg-[#d4af37]/10 px-4 py-3 text-left font-body text-sm font-semibold text-gold">
            {user ? "My Account" : "Login / Sign up"}
          </button>
        </motion.div>
      )}
    </>
  );
}

/* ============ Hero ============ */
function Hero() {
  const { openBooking } = useDialog();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  const getChecklist = () => go("freebie");

  return (
    <section id="top" ref={ref} className="relative overflow-hidden px-6 pb-16 pt-28">
      <motion.div style={{ y }} className="spotlight pointer-events-none absolute inset-0" />
      <div className="mx-auto grid w-full max-w-6xl items-center gap-10 lg:grid-cols-2">
        {/* Left: copy */}
        <div className="relative z-10">
          <Reveal>
            <span className="glass inline-flex items-center gap-2 rounded-full px-4 py-2 font-body text-xs text-[#c9c9c9]">
              <BadgeCheck className="h-4 w-4 text-gold" /> Trusted credit education & restoration
            </span>
          </Reveal>
          <div className="mt-5 space-y-1">
            {["Take control of", "your credit &", "financial future."].map((t, i) => (
              <div key={i} className="overflow-hidden">
                <motion.h1 initial={{ y: "110%" }} animate={{ y: "0%" }}
                  transition={{ delay: 0.15 + i * 0.1, duration: 0.85, ease: [0.22, 1, 0.36, 1] }}
                  className="font-display text-4xl font-light tracking-tighter-x text-[#f5f5f5] sm:text-5xl lg:text-6xl">
                  {i === 2 ? <span className="gold-text-gradient font-normal">{t}</span> : t}
                </motion.h1>
              </div>
            ))}
          </div>
          <Reveal delay={0.5}>
            <p className="mt-6 max-w-lg font-body text-base leading-relaxed text-[#a3a3a3]">
              BGM Financial Solutions is a personalized financial institution for credit restoration,
              education and wealth building — Build, Grow, Maintain.
            </p>
          </Reveal>
          <Reveal delay={0.6}>
            <div className="mt-7 flex flex-wrap gap-4">
              <GoldButton testid="hero-consult-btn" onClick={() => openBooking("Free Consultation", "Book your free consultation")} className="px-8 py-4 text-base">
                Book free consultation
              </GoldButton>
              <GhostButton testid="hero-freebie-btn" onClick={getChecklist} className="px-8 py-4 text-base">
                <Download className="h-4 w-4" /> Free checklist
              </GhostButton>
            </div>
          </Reveal>
          {/* visible service quick-tiles — options without opening the menu */}
          <Reveal delay={0.7}>
            <div className="mt-8">
              <p className="overline mb-3">What we provide</p>
              <div className="flex flex-wrap gap-2">
                {SERVICE_LINKS.map((s) => (
                  <button key={s.label} data-testid={`hero-service-${s.label.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => go(s.id)}
                    className="glass group inline-flex items-center gap-2 rounded-full px-4 py-2 font-body text-xs text-[#c9c9c9] transition-[border-color,color] duration-300 hover:border-[#d4af37]/50 hover:text-gold">
                    <s.icon className="h-3.5 w-3.5 text-gold" /> {s.label}
                  </button>
                ))}
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.8}>
            <div className="mt-8 flex flex-wrap gap-8">
              {[["1k+", "Clients guided"], ["3", "Bureaus covered"], ["45 days", "First results*"]].map(([n, l]) => (
                <div key={l}>
                  <div className="font-display text-3xl font-light gold-text-gradient">{n}</div>
                  <div className="mt-1 font-body text-xs text-[#888]">{l}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

        {/* Right: team image */}
        <Reveal delay={0.4}>
          <div className="relative z-10">
            <div className="spotlight pointer-events-none absolute -inset-8" />
            <div className="relative overflow-hidden rounded-[2rem] border border-[#d4af37]/25 shadow-[0_30px_90px_rgba(0,0,0,0.6)]">
              <img src={IMG.trio} alt="The BGM Financial Solutions team" className="h-[520px] w-full object-cover object-top" data-testid="hero-team-img" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 flex items-center justify-between">
                <span className="glass rounded-full px-4 py-2 font-body text-xs text-[#f5f5f5]">The BGM Team</span>
                <span className="glass rounded-full px-4 py-2 font-body text-xs text-gold">Build · Grow · Maintain</span>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ============ Why Choose BGM ============ */
const WHY = [
  { icon: Award, title: "Results-driven", desc: "A clear, personalized strategy — not empty promises." },
  { icon: ShieldCheck, title: "Consumer-law backed", desc: "Disputes grounded in your rights as a consumer." },
  { icon: Users, title: "Real human support", desc: "Guidance and updates at every step of the journey." },
  { icon: Clock, title: "Ongoing rounds", desc: "New dispute rounds every 30–45 days until it's right." },
];
function WhyChoose() {
  return (
    <section className="relative px-6 py-24">
      <div className="mx-auto max-w-6xl">
        <Reveal className="mb-12"><Overline>Why choose BGM</Overline>
          <h2 className="mt-4 font-display text-3xl font-light tracking-tighter-x text-[#f5f5f5] sm:text-5xl">A financial institution that feels personal</h2>
        </Reveal>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {WHY.map((w, i) => (
            <Reveal key={w.title} delay={i * 0.08}>
              <div className="glass h-full rounded-2xl p-7" data-testid={`why-${i}`}>
                <span className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl border border-[#d4af37]/30 bg-[#d4af37]/10 text-gold"><w.icon className="h-6 w-6" /></span>
                <h3 className="font-display text-lg text-[#f5f5f5]">{w.title}</h3>
                <p className="mt-2 font-body text-sm text-[#a3a3a3]">{w.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============ Freebie: Credit Clarity Checklist ============ */
const CHECKLIST = [
  "Pull all 3 credit reports (Experian, Equifax, TransUnion)",
  "Identify every negative & inaccurate item",
  "Verify personal info & account details for errors",
  "Flag duplicate, outdated or unverifiable accounts",
  "Track your credit utilization under 30%",
  "Build a monthly on-time payment system",
  "Draft your first dispute strategy",
  "Set 30 / 60 / 90-day credit goals",
];
function Freebie() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try { await createLead({ name: name || "Checklist", email, interest: "Credit Clarity Checklist" }); } catch {}
    setUnlocked(true);
  };
  const download = async () => {
    try {
      const info = await checklistInfo();
      if (info.available) { window.open(CHECKLIST_DOWNLOAD_URL, "_blank"); return; }
    } catch {}
    const text = "BGM FINANCIAL SOLUTIONS — CREDIT CLARITY CHECKLIST\nBuild. Grow. Maintain.\n\n" +
      CHECKLIST.map((c, i) => `${i + 1}. [ ] ${c}`).join("\n") +
      "\n\nDisclaimer: BGM provides credit education & empowerment resources. Results vary; no specific outcomes guaranteed.";
    const url = URL.createObjectURL(new Blob([text], { type: "text/plain" }));
    const a = document.createElement("a"); a.href = url; a.download = "BGM-Credit-Clarity-Checklist.txt"; a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <section id="freebie" className="relative px-6 py-24">
      <div className="mx-auto max-w-6xl">
        <div className="glass-solid gold-border-glow relative overflow-hidden rounded-[2rem] p-8 sm:p-12">
          <div className="spotlight pointer-events-none absolute inset-0" />
          <div className="relative z-10 grid items-center gap-10 lg:grid-cols-2">
            <div>
              <span className="glass inline-flex items-center gap-2 rounded-full px-4 py-1.5 font-body text-xs text-gold"><Sparkles className="h-4 w-4" /> Free gift</span>
              <h2 className="mt-5 font-display text-3xl font-light tracking-tighter-x text-[#f5f5f5] sm:text-4xl">The Credit Clarity Checklist</h2>
              <p className="mt-4 max-w-md font-body text-[#a3a3a3]">
                An 8-step blueprint to understand your credit and take your first confident steps —
                the same foundation we use with clients. Yours free.
              </p>
              {!unlocked ? (
                <form onSubmit={submit} className="mt-7 space-y-3" data-testid="freebie-form">
                  <input required placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} data-testid="freebie-name"
                    className="w-full rounded-xl border border-white/10 bg-black/40 px-4 py-3 font-body text-sm text-[#f5f5f5] outline-none focus:border-[#d4af37]" />
                  <div className="flex gap-2">
                    <input required type="email" placeholder="Your email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="freebie-email"
                      className="flex-1 rounded-xl border border-white/10 bg-black/40 px-4 py-3 font-body text-sm text-[#f5f5f5] outline-none focus:border-[#d4af37]" />
                    <GoldButton testid="freebie-submit" type="submit">Unlock</GoldButton>
                  </div>
                  <p className="flex items-center gap-1.5 font-body text-[11px] text-[#666]"><Lock className="h-3 w-3" /> No spam. Unsubscribe anytime.</p>
                </form>
              ) : (
                <div className="mt-7" data-testid="freebie-unlocked">
                  <p className="font-body text-sm text-gold">Unlocked! Download below — a copy is also on its way to your inbox.</p>
                  <GoldButton testid="freebie-download" className="mt-4" onClick={download}><Download className="h-4 w-4" /> Download checklist</GoldButton>
                </div>
              )}
            </div>
            <div className="glass rounded-2xl p-7">
              <div className="mb-4 flex items-center gap-2"><FileCheck className="h-5 w-5 text-gold" /><span className="font-display text-lg text-[#f5f5f5]">Inside the checklist</span></div>
              <ul className="space-y-3">
                {CHECKLIST.map((c, i) => (
                  <li key={i} className={`flex items-start gap-3 font-body text-sm ${unlocked ? "text-[#e0e0e0]" : "text-[#777]"}`}>
                    <span className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border ${unlocked ? "border-[#d4af37] bg-[#d4af37]/15 text-gold" : "border-white/15"}`}>
                      {unlocked ? <Check className="h-3 w-3" /> : <span className="text-[10px]">{i + 1}</span>}
                    </span>
                    {c}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============ What We Help You Tackle ============ */
const TACKLE = ["Late Payments", "Collections", "Charge-Offs", "Hard Inquiries", "Repossessions", "Public Records", "Medical Bills", "Judgments", "Bankruptcies", "Personal Info Errors"];
function WhatWeHelp() {
  return (
    <section className="relative px-6 py-24">
      <div className="mx-auto max-w-6xl">
        <Reveal className="mb-10"><Overline>What we help you tackle</Overline>
          <h2 className="mt-4 font-display text-3xl font-light tracking-tighter-x text-[#f5f5f5] sm:text-5xl">Items we help you review & dispute</h2>
        </Reveal>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {TACKLE.map((t, i) => (
            <Reveal key={t} delay={(i % 5) * 0.05}>
              <div className="glass flex items-center gap-2 rounded-xl px-4 py-4 font-body text-sm text-[#c9c9c9]" data-testid={`tackle-${i}`}>
                <BadgeCheck className="h-4 w-4 flex-shrink-0 text-gold" /> {t}
              </div>
            </Reveal>
          ))}
        </div>
        <p className="mt-6 font-body text-xs text-[#666]">We provide education & consumer-law based dispute support for inaccurate, outdated or unverifiable items. Results vary.</p>
      </div>
    </section>
  );
}

/* ============ How It Works ============ */
const STEPS = [
  { n: "01", t: "Book or unlock", d: "Grab your free consultation or the Credit Clarity Checklist to begin." },
  { n: "02", t: "Review your profile", d: "We analyze all 3 bureau reports and build your personalized strategy." },
  { n: "03", t: "We dispute", d: "Consumer-law based dispute rounds sent every 30–45 days on your behalf." },
  { n: "04", t: "Build & maintain", d: "Education, coaching and habits to sustain and grow your credit." },
];
function Process() {
  return (
    <section id="process" className="relative px-6 py-24">
      <div className="mx-auto max-w-6xl">
        <Reveal className="mb-12"><Overline>How it works</Overline>
          <h2 className="mt-4 font-display text-3xl font-light tracking-tighter-x text-[#f5f5f5] sm:text-5xl">A clear path forward</h2>
        </Reveal>
        <div className="grid gap-5 md:grid-cols-4">
          {STEPS.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.1}>
              <div className="glass h-full rounded-2xl p-7" data-testid={`step-${i}`}>
                <span className="font-editorial text-5xl italic text-[#d4af37]/40">{s.n}</span>
                <h3 className="mt-3 font-display text-lg text-[#f5f5f5]">{s.t}</h3>
                <p className="mt-2 font-body text-sm text-[#a3a3a3]">{s.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============ Start Here ============ */
const PATHS = [
  { icon: CreditCard, title: "Fix My Credit", desc: "Dispute strategy & credit restoration.", tag: "Credit Repair" },
  { icon: TrendingUp, title: "Build My Business", desc: "Business credit & funding readiness.", tag: "Business Funding Education" },
  { icon: GraduationCap, title: "Learn Financial Freedom", desc: "Wealth literacy & digital guides.", tag: "Credit Education" },
  { icon: Crown, title: "Join BGM Community", desc: "Courses, masterclasses & lounge.", tag: "BGM Membership" },
];
function StartHere() {
  const { openLead } = useDialog();
  return (
    <section id="start-here" className="relative px-6 py-28">
      <div className="mx-auto max-w-6xl">
        <Reveal><Overline>Start here</Overline>
          <h2 className="mt-4 font-display text-3xl font-light tracking-tighter-x text-[#f5f5f5] sm:text-5xl">Choose your path</h2>
          <p className="mt-4 max-w-xl font-body text-[#a3a3a3]">BGM is a financial ecosystem. Tell us where you want to begin and we'll guide the rest.</p>
        </Reveal>
        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {PATHS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.08}>
              <button data-testid={`path-${i}`} onClick={() => openLead(p.tag, p.title)}
                className="glass group flex h-full w-full flex-col items-start rounded-2xl p-7 text-left transition-[border-color,transform,background-color] duration-500 hover:-translate-y-2 hover:border-[#d4af37]/50 hover:bg-black/70">
                <span className="mb-6 flex h-12 w-12 items-center justify-center rounded-xl border border-[#d4af37]/30 bg-[#d4af37]/10 text-gold transition-colors group-hover:bg-[#d4af37] group-hover:text-[#050505]">
                  <p.icon className="h-6 w-6" />
                </span>
                <h3 className="font-display text-xl text-[#f5f5f5]">{p.title}</h3>
                <p className="mt-2 font-body text-sm text-[#a3a3a3]">{p.desc}</p>
                <span className="mt-5 inline-flex items-center gap-1 font-body text-xs font-semibold text-gold opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                  Begin <ArrowRight className="h-3 w-3" />
                </span>
              </button>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============ Rooms (bento) ============ */
const ROOMS = [
  { id: "credit", img: IMG.vault, kicker: "01 · Credit Restoration Room", title: "Restore Your Credit. Rebuild Your Options.", body: "A luxury vault of dispute strategy, personalized plans and progress tracking.", cta: "Start your credit journey", tag: "Credit Repair", span: "lg:col-span-3", h: "h-[440px]" },
  { id: "education", img: IMG.library, kicker: "02 · Financial Education Hub", title: "Access the Wealth Library", body: "Glowing digital guides, credit literacy & business funding education.", cta: "Access the wealth library", tag: "Credit Education", span: "lg:col-span-2", h: "h-[440px]" },
  { id: "business", img: IMG.boardroom, kicker: "03 · Business Funding Center", title: "Build Your Business", body: "A futuristic boardroom of funding readiness & entrepreneur resources.", cta: "Build your business", tag: "Business Funding Education", span: "lg:col-span-2", h: "h-[380px]" },
  { id: "membership", img: IMG.lounge, kicker: "04 · BGM Membership", title: "Join The Community", body: "A private members lounge: courses, masterclasses & exclusive resources.", cta: "Join the community", tag: "BGM Membership", span: "lg:col-span-3", h: "h-[380px]" },
];
function Rooms() {
  const { openLead } = useDialog();
  return (
    <section className="relative px-6 py-16">
      <div className="mx-auto max-w-6xl">
        <Reveal className="mb-12"><Overline>The virtual headquarters</Overline>
          <h2 className="mt-4 font-display text-3xl font-light tracking-tighter-x text-[#f5f5f5] sm:text-5xl">Explore the floors</h2>
        </Reveal>
        <div className="grid gap-5 lg:grid-cols-5">
          {ROOMS.map((r, i) => (
            <motion.div key={r.id} id={r.id}
              initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.8, delay: (i % 2) * 0.1, ease: [0.22, 1, 0.36, 1] }}
              className={`group relative overflow-hidden rounded-3xl border border-white/10 ${r.span} ${r.h}`} data-testid={`room-${r.id}`}>
              <div className="absolute inset-0 bg-cover bg-center transition-transform duration-[1.2s] group-hover:scale-110"
                style={{ backgroundImage: `url('${r.img}')` }} />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-[#050505]/20" />
              <div className="absolute inset-0 flex flex-col justify-end p-8">
                <span className="overline mb-3">{r.kicker}</span>
                <h3 className="font-display text-2xl tracking-tighter-x text-[#f5f5f5] sm:text-3xl">{r.title}</h3>
                <p className="mt-3 max-w-md font-body text-sm text-[#c9c9c9]">{r.body}</p>
                <div className="mt-6">
                  <GoldButton testid={`room-cta-${r.id}`} onClick={() => openLead(r.tag, r.title)}>{r.cta}</GoldButton>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============ Journey timeline ============ */
const JOURNEY = [
  { n: "01", word: "BUILD", desc: "Create the foundation. We review your credit profile and design a personalized strategy from the ground up." },
  { n: "02", word: "GROW", desc: "Increase your knowledge, income and opportunities through education, funding readiness and coaching." },
  { n: "03", word: "MAINTAIN", desc: "Protect your financial future with the habits, tools and community to sustain generational wealth." },
];
function Journey() {
  return (
    <section id="journey" className="relative px-6 py-28">
      <div className="mx-auto max-w-5xl">
        <Reveal className="mb-16 text-center"><Overline>The BGM journey</Overline>
          <h2 className="mt-4 font-display text-3xl font-light tracking-tighter-x text-[#f5f5f5] sm:text-5xl">Build. Grow. Maintain.</h2>
        </Reveal>
        <div className="relative border-l border-white/10 pl-8 md:pl-0">
          {JOURNEY.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.1}>
              <div className="relative mb-16 md:grid md:grid-cols-12 md:gap-8">
                <div className="md:col-span-4">
                  <div className="mb-3 flex items-baseline gap-3">
                    <span className="font-editorial text-6xl italic text-[#d4af37]/40">{s.n}</span>
                    <span className="font-display text-3xl font-light tracking-widest text-gold">{s.word}</span>
                  </div>
                </div>
                <p className="font-body text-lg leading-relaxed text-[#c9c9c9] md:col-span-8">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============ Services 3D tilt cards ============ */
const SERVICES = [
  { icon: ShieldCheck, title: "Credit Repair", desc: "Personalized dispute strategy & restoration." },
  { icon: BookOpen, title: "Credit Education", desc: "Understand and control your credit for life." },
  { icon: Briefcase, title: "Business Funding Education", desc: "Business credit & funding readiness." },
  { icon: Package, title: "Digital Products", desc: "Guides, templates & wealth resources." },
  { icon: Compass, title: "Financial Coaching", desc: "Private strategy sessions & accountability." },
];
function TiltCard({ s, i }) {
  const { openLead } = useDialog();
  const x = useMotionValue(0), y = useMotionValue(0);
  const rx = useSpring(useTransform(y, [-0.5, 0.5], [10, -10]), { stiffness: 200, damping: 20 });
  const ry = useSpring(useTransform(x, [-0.5, 0.5], [-10, 10]), { stiffness: 200, damping: 20 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    x.set((e.clientX - r.left) / r.width - 0.5);
    y.set((e.clientY - r.top) / r.height - 0.5);
  };
  return (
    <motion.div
      onMouseMove={onMove} onMouseLeave={() => { x.set(0); y.set(0); }}
      style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d", transformPerspective: 900 }}
      initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      transition={{ duration: 0.7, delay: i * 0.07 }}
      className="glass group relative rounded-2xl p-8 transition-[border-color] duration-500 hover:border-[#d4af37]/50"
      data-testid={`service-${i}`}>
      <div style={{ transform: "translateZ(40px)" }}>
        <span className="mb-6 flex h-12 w-12 items-center justify-center rounded-xl border border-[#d4af37]/30 bg-[#d4af37]/10 text-gold">
          <s.icon className="h-6 w-6" />
        </span>
        <h3 className="font-display text-xl text-[#f5f5f5]">{s.title}</h3>
        <p className="mt-2 font-body text-sm text-[#a3a3a3]">{s.desc}</p>
        <button onClick={() => openLead(s.title, s.title)} data-testid={`service-cta-${i}`}
          className="mt-6 inline-flex items-center gap-1 font-body text-xs font-semibold text-gold transition-transform duration-300 hover:translate-x-1">
          Learn more <ArrowRight className="h-3 w-3" />
        </button>
      </div>
    </motion.div>
  );
}
function Services() {
  return (
    <section id="services" className="relative px-6 py-28">
      <div className="mx-auto max-w-6xl">
        <Reveal className="mb-14"><Overline>What we offer</Overline>
          <h2 className="mt-4 font-display text-3xl font-light tracking-tighter-x text-[#f5f5f5] sm:text-5xl">Our services</h2>
        </Reveal>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s, i) => <TiltCard key={s.title} s={s} i={i} />)}
        </div>
      </div>
    </section>
  );
}

/* ============ Marquee ============ */
function EditorialMarquee() {
  return (
    <div className="border-y border-white/10 py-8" data-testid="marquee">
      <Marquee speed={28} gradient={false} autoFill>
        {["Build.", "Grow.", "Maintain.", "Credit Empowerment.", "Generational Wealth.", "Financial Freedom."].map((t, i) => (
          <span key={i} className="mx-8 font-editorial text-4xl italic text-[#f5f5f5]/70 sm:text-6xl">
            {t} <span className="text-gold">✦</span>
          </span>
        ))}
      </Marquee>
    </div>
  );
}

/* ============ Pricing ============ */
const PLANS = [
  { name: "Basic Credit Restoration", lookup: "bgm_basic", price: "75", sub: "One-Time Enrollment", best: "A strong foundation for a smaller number of negative items.",
    features: ["Credit profile review", "Personalized dispute strategy", "Up to 6 dispute rounds", "Credit education resources", "Progress tracking support"], cta: "Start rebuilding my credit", featured: false },
  { name: "Premium Credit Transformation", lookup: "bgm_premium", price: "199", sub: "One-Time Enrollment", best: "Multiple negative accounts needing an aggressive strategy.",
    features: ["Everything in Basic", "Up to 12 dispute rounds", "Personalized credit action plan", "Debt education guidance", "Monthly progress reviews", "Priority client support"], cta: "Create my credit plan", featured: true },
  { name: "VIP Wealth Building Experience", lookup: "bgm_vip", price: "499", sub: "One-Time Enrollment", best: "A complete financial transformation experience.",
    features: ["Everything in Premium", "Full credit restoration roadmap", "Business credit education", "Funding preparation guidance", "Financial literacy training", "Private strategy sessions", "Exclusive BGM resources"], cta: "Unlock the VIP experience", featured: false },
];
function Pricing() {
  const [loading, setLoading] = useState(null);
  const buy = async (p) => {
    setLoading(p.lookup);
    try {
      const { checkout_url } = await createCheckout({ lookup_key: p.lookup, origin_url: window.location.origin });
      window.location.href = checkout_url;
    } catch {
      setLoading(null);
    }
  };
  return (
    <section id="pricing" className="relative px-6 py-28">
      <div className="spotlight pointer-events-none absolute inset-x-0 top-0 h-1/2" />
      <div className="mx-auto max-w-6xl">
        <Reveal className="mb-14 text-center"><Overline>Choose your plan</Overline>
          <h2 className="mt-4 font-display text-3xl font-light tracking-tighter-x text-[#f5f5f5] sm:text-5xl">Luxury financial dashboard</h2>
        </Reveal>
        <div className="grid gap-6 lg:grid-cols-3">
          {PLANS.map((p, i) => (
            <Reveal key={p.name} delay={i * 0.1}>
              <div data-testid={`plan-${i}`}
                className={`relative flex h-full flex-col rounded-3xl p-8 ${p.featured ? "gold-border-glow bg-[#0f0f10]" : "glass"}`}>
                {p.featured && <span className="absolute -top-3 left-8 rounded-full bg-[#d4af37] px-3 py-1 font-body text-[10px] font-bold tracking-widest text-[#050505]">MOST POPULAR</span>}
                <h3 className="font-display text-lg text-[#f5f5f5]">{p.name}</h3>
                <div className="mt-4 flex items-baseline gap-2">
                  <span className="font-display text-5xl font-light gold-text-gradient">${p.price}</span>
                  <span className="font-body text-xs text-[#888]">{p.sub}</span>
                </div>
                <p className="mt-4 font-body text-sm text-[#a3a3a3]">{p.best}</p>
                <ul className="mt-6 flex-1 space-y-3">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 font-body text-sm text-[#c9c9c9]">
                      <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-gold" /> {f}
                    </li>
                  ))}
                </ul>
                <div className="mt-8">
                  {p.featured
                    ? <GoldButton testid={`plan-cta-${i}`} className="w-full justify-center" onClick={() => buy(p)} disabled={loading === p.lookup}>{loading === p.lookup ? "Redirecting…" : p.cta}</GoldButton>
                    : <GhostButton testid={`plan-cta-${i}`} className="w-full justify-center" onClick={() => buy(p)} disabled={loading === p.lookup}>{loading === p.lookup ? "Redirecting…" : p.cta} <ArrowRight className="h-4 w-4" /></GhostButton>}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
        <p className="mx-auto mt-10 max-w-2xl text-center font-body text-xs text-[#666]">Secure Stripe checkout. Test mode is active — use card 4242 4242 4242 4242, any future expiry & CVC.</p>
      </div>
    </section>
  );
}

/* ============ Success Wall + Testimonials ============ */
const WINS = ["Credit education completed", "Negative accounts reviewed", "Financial goals created", "Business foundations built", "Clients empowered through knowledge"];
const REVIEWS = [
  { q: "BGM helped me finally understand my credit instead of feeling overwhelmed by it. The education and guidance gave me a clear plan to move forward.", a: "Jordan R." },
  { q: "I went from feeling stuck financially to having a better understanding of my options. BGM made the process simple and gave me confidence.", a: "Taylor M." },
  { q: "The biggest difference was the knowledge. I didn't just want someone to fix my credit — I wanted to understand how to maintain it and build from there.", a: "Marcus D." },
];
function SuccessWall() {
  const RESULTS = [
    { img: IMG.score2, label: "+149 points", sub: "Client credit score increase" },
    { img: IMG.score1, label: "+37 points", sub: "Score climbing to 662 (Fair)" },
  ];
  return (
    <section className="relative px-6 py-28">
      <div className="mx-auto max-w-6xl">
        <Reveal className="mb-12"><Overline>The BGM success wall</Overline>
          <h2 className="mt-4 font-display text-3xl font-light tracking-tighter-x text-[#f5f5f5] sm:text-5xl">Real client results</h2>
        </Reveal>
        <div className="mb-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {RESULTS.map((r, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div className="glass overflow-hidden rounded-2xl" data-testid={`result-${i}`}>
                <div className="relative">
                  <img src={r.img} alt={r.sub} className="h-72 w-full object-cover object-top" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0f0f10] to-transparent" />
                </div>
                <div className="flex items-center justify-between p-5">
                  <div>
                    <div className="font-display text-2xl font-light gold-text-gradient">{r.label}</div>
                    <div className="mt-1 font-body text-xs text-[#888]">{r.sub}</div>
                  </div>
                  <TrendingUp className="h-6 w-6 text-gold" />
                </div>
              </div>
            </Reveal>
          ))}
          {WINS.slice(0, 2).map((w, i) => (
            <Reveal key={w} delay={(i + 2) * 0.1}>
              <div className="glass flex h-full flex-col justify-center rounded-2xl p-6" data-testid={`win-card-${i}`}>
                <BadgeCheck className="mb-3 h-7 w-7 text-gold" />
                <p className="font-display text-lg leading-snug text-[#f5f5f5]">{w}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <div className="mb-10 flex flex-wrap gap-3">
          {WINS.map((w, i) => (
            <Reveal key={w} delay={i * 0.05}>
              <span className="glass inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-body text-sm text-[#c9c9c9]">
                <Check className="h-4 w-4 text-gold" /> {w}
              </span>
            </Reveal>
          ))}
        </div>
        <div className="grid gap-5 md:grid-cols-3">
          {REVIEWS.map((r, i) => (
            <Reveal key={r.a} delay={i * 0.1}>
              <div className="glass h-full rounded-2xl p-7" data-testid={`review-${i}`}>
                <Quote className="mb-4 h-7 w-7 text-[#d4af37]/50" />
                <div className="mb-3 flex gap-0.5">{[...Array(5)].map((_, k) => <Star key={k} className="h-4 w-4 fill-[#d4af37] text-[#d4af37]" />)}</div>
                <p className="font-editorial text-lg italic leading-relaxed text-[#e0e0e0]">"{r.q}"</p>
                <p className="mt-5 font-body text-sm font-semibold text-gold">— {r.a}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <p className="mt-8 font-body text-xs text-[#666]">Results shown are individual client outcomes and are not guarantees. Results vary depending on individual circumstances.</p>
      </div>
    </section>
  );
}

/* ============ Brand Banner ============ */
function BrandBanner() {
  const { openBooking } = useDialog();
  return (
    <section className="relative px-6 py-16">
      <div className="mx-auto max-w-5xl">
        <Reveal>
          <div className="relative overflow-hidden rounded-[2rem] border border-[#d4af37]/25">
            <img src={IMG.banner} alt="Your credit is protected by law — Mariah Pemberton, CEO of BGM" className="w-full object-contain" data-testid="brand-banner-img" />
          </div>
          <div className="mt-6 flex justify-center">
            <GoldButton testid="banner-cta" onClick={() => openBooking("Free Consultation", "Book your free consultation")}>Know your rights — book a consult</GoldButton>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ============ Founder ============ */
function Founder() {
  return (
    <section id="about" className="relative px-6 py-28">
      <div className="mx-auto grid max-w-6xl items-center gap-12 lg:grid-cols-2">
        <Reveal>
          <div className="relative">
            <div className="spotlight pointer-events-none absolute -inset-8" />
            <div className="relative overflow-hidden rounded-3xl border border-[#d4af37]/20">
              <img src={IMG.founder} alt="Mariah Pemberton, Founder & CEO" className="h-[560px] w-full object-cover object-top" data-testid="founder-img" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/60 to-transparent" />
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <Overline>The founder</Overline>
          <h2 className="mt-4 font-display text-3xl font-light tracking-tighter-x text-[#f5f5f5] sm:text-5xl">Mariah Pemberton</h2>
          <p className="mt-2 font-body text-sm font-semibold text-gold">Founder & CEO of BGM Financial Solutions</p>
          <p className="mt-6 font-editorial text-xl italic leading-relaxed text-[#c9c9c9]">
            "Mariah Pemberton founded BGM Financial Solutions with a mission to help individuals take
            control of their financial future through credit education, financial empowerment, and
            business growth strategies."
          </p>
          <p className="mt-5 font-body leading-relaxed text-[#a3a3a3]">
            BGM was created around three principles: Build. Grow. Maintain. The goal is to provide
            people with the knowledge, tools, and confidence needed to make smarter financial
            decisions and create new opportunities.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

/* ============ Contact ============ */
function Contact() {
  const { openBooking, openLead } = useDialog();
  return (
    <section id="contact" className="relative px-6 py-28">
      <div className="mx-auto max-w-4xl">
        <div className="glass-solid gold-border-glow relative overflow-hidden rounded-[2rem] p-10 text-center sm:p-16">
          <div className="spotlight pointer-events-none absolute inset-0" />
          <div className="relative z-10">
            <Overline>Your financial future starts now</Overline>
            <h2 className="mx-auto mt-5 max-w-2xl font-display text-3xl font-light tracking-tighter-x text-[#f5f5f5] sm:text-5xl">
              Ready to <span className="gold-text-gradient">Build, Grow & Maintain?</span>
            </h2>
            <p className="mx-auto mt-5 max-w-lg font-body text-[#a3a3a3]">
              Book a free consultation or join the movement. A BGM strategist will guide your next step.
            </p>
            <div className="mt-9 flex flex-wrap justify-center gap-4">
              <GoldButton testid="contact-book-btn" onClick={() => openBooking("Free Consultation", "Book your free consultation")} className="px-9 py-4 text-base">Book free consultation</GoldButton>
              <GhostButton testid="contact-join-btn" onClick={() => openLead("BGM Membership", "Join the BGM community")} className="px-9 py-4 text-base">Join the community</GhostButton>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============ Footer ============ */
function Footer() {
  const [email, setEmail] = useState("");
  const [ok, setOk] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { subscribe } = await import("../lib/api");
      await subscribe(email); setOk(true);
    } catch { setOk(true); }
  };
  return (
    <footer className="border-t border-white/10 px-6 py-16">
      <div className="mx-auto max-w-6xl">
        <div className="grid gap-10 md:grid-cols-2">
          <div>
            <span className="font-display text-3xl font-bold tracking-[0.15em] text-gold">BGM</span>
            <p className="mt-4 max-w-sm font-body text-sm text-[#a3a3a3]">
              BGM Financial Solutions — a luxury financial ecosystem for credit restoration,
              education and generational wealth. Build. Grow. Maintain.
            </p>
          </div>
          <div>
            <p className="overline mb-4">Join the wealth list</p>
            {ok ? (
              <p className="font-body text-sm text-gold" data-testid="newsletter-success">You're subscribed. Welcome to the movement.</p>
            ) : (
              <form onSubmit={submit} className="flex gap-2" data-testid="newsletter-form">
                <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email" data-testid="newsletter-email"
                  className="flex-1 rounded-full border border-white/10 bg-black/40 px-5 py-3 font-body text-sm text-[#f5f5f5] placeholder-[#666] outline-none focus:border-[#d4af37]" />
                <GoldButton testid="newsletter-submit" type="submit">Subscribe</GoldButton>
              </form>
            )}
          </div>
        </div>
        <div className="mt-12 border-t border-white/10 pt-8">
          <p className="font-body text-xs leading-relaxed text-[#666]">
            BGM Financial Solutions provides credit education and financial empowerment resources.
            Results vary depending on individual circumstances. We do not guarantee specific credit
            score increases or financial outcomes.
          </p>
          <p className="mt-4 font-body text-xs text-[#555]">© {new Date().getFullYear()} BGM Financial Solutions. Build. Grow. Maintain.</p>
        </div>
      </div>
    </footer>
  );
}

/* ============ Page ============ */
export default function Experience() {
  return (
    <div className="relative bg-[#050505]">
      <FloatingNav />
      <Hero />
      <WhyChoose />
      <Services />
      <Freebie />
      <WhatWeHelp />
      <Process />
      <BrandBanner />
      <StartHere />
      <Rooms />
      <EditorialMarquee />
      <Journey />
      <Catalog />
      <SuccessWall />
      <Founder />
      <Contact />
      <Footer />
      <ChatWidget />
    </div>
  );
}

